import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ParaclimbingPage from './pages/ParaclimbingPage';
import BelgianTeamPage from './pages/BelgianTeamPage';
import ActivitiesPage from './pages/ActivitiesPage';
import SupportUsPage from './pages/SupportUsPage';
import ContactPage from './pages/ContactPage';
import BK2026Page from './pages/BK2026Page';
import SpeedSchedulePage from './pages/SpeedSchedulePage';
import SpeedAdminPage from './pages/SpeedAdminPage';
import BoulderAdminPage from './pages/BoulderAdminPage';
import BoulderResultsPage from './pages/BoulderResultsPage';
import LeadAdminPage from './pages/LeadAdminPage';
import LeadResultsPage from './pages/LeadResultsPage';
import ResultsHubPage from './pages/ResultsHubPage';
import JuryHubPage from './pages/JuryHubPage';
import AthletesPage from './pages/AthletesPage';
import AthleteProfilePage from './pages/AthleteProfilePage';
import SchedulePage from './pages/SchedulePage';
import LeadJuryPrintPage from './pages/LeadJuryPrintPage';
import NotFoundPage from './pages/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import CMSModal from './components/CMSModal';
import CompetitionSetupPage from './pages/CompetitionSetupPage';
import CompetitionHubPage from './pages/CompetitionHubPage';
import CompetitionsListPage from './pages/CompetitionsListPage';
import { CompetitionLayout, LegacyBK2026Provider } from './context/CompetitionContext';

const App: React.FC = () => {
  const [cmsOpen, setCmsOpen] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setCmsOpen(open => !open);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <ErrorBoundary>
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <main className="flex-grow">
            <Routes>
              {/* ── Site pages ───────────────────────────────────────── */}
              <Route path="/" element={<HomePage />} />
              <Route path="/paraclimbing" element={<ParaclimbingPage />} />
              <Route path="/belgian-team" element={<BelgianTeamPage />} />
              <Route path="/activities" element={<ActivitiesPage />} />
              <Route path="/support-us" element={<SupportUsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/bk-2026" element={<BK2026Page />} />
              <Route path="/athletes" element={<AthletesPage />} />
              <Route path="/athletes/:slug" element={<AthleteProfilePage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/results" element={<ResultsHubPage />} />

              {/* ── Competition setup ────────────────────────────────── */}              <Route path="/competitions" element={<CompetitionsListPage />} />              <Route path="/competition/new" element={<CompetitionSetupPage />} />

              {/* ── Per-competition routes (nested under CompetitionLayout) ── */}
              <Route path="/competition/:competitionId" element={<CompetitionLayout />}>
                <Route index element={<CompetitionHubPage />} />
                <Route path="jury"             element={<JuryHubPage />} />
                <Route path="boulder-admin"    element={<BoulderAdminPage />} />
                <Route path="lead-admin"       element={<LeadAdminPage />} />
                <Route path="speed-admin"      element={<SpeedAdminPage />} />
                <Route path="results/boulder"  element={<BoulderResultsPage />} />
                <Route path="results/lead"     element={<LeadResultsPage />} />
                <Route path="speed-schedule"   element={<SpeedSchedulePage />} />
                <Route path="lead-jury-print"  element={<LeadJuryPrintPage />} />
              </Route>

              {/* ── Legacy BK2026 routes (backward-compatible bookmarks) ── */}
              <Route path="/jury" element={
                <LegacyBK2026Provider><JuryHubPage /></LegacyBK2026Provider>
              } />
              <Route path="/boulder-admin" element={
                <LegacyBK2026Provider><BoulderAdminPage /></LegacyBK2026Provider>
              } />
              <Route path="/boulder-results" element={
                <LegacyBK2026Provider><BoulderResultsPage /></LegacyBK2026Provider>
              } />
              <Route path="/lead-admin" element={
                <LegacyBK2026Provider><LeadAdminPage /></LegacyBK2026Provider>
              } />
              <Route path="/lead-results" element={
                <LegacyBK2026Provider><LeadResultsPage /></LegacyBK2026Provider>
              } />
              <Route path="/speed-admin" element={
                <LegacyBK2026Provider><SpeedAdminPage /></LegacyBK2026Provider>
              } />
              <Route path="/speed-schedule" element={
                <LegacyBK2026Provider><SpeedSchedulePage /></LegacyBK2026Provider>
              } />
              <Route path="/lead-jury-print" element={
                <LegacyBK2026Provider><LeadJuryPrintPage /></LegacyBK2026Provider>
              } />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
          <Footer />
        </div>
        {cmsOpen && <CMSModal onClose={() => setCmsOpen(false)} />}
      </ErrorBoundary>
    </Router>
  );
};

export default App;
